<template>
  <div class="container">
    <!-- 탭 -->
    <div class="tabs">
      <div
        class="tab"
        :class="{ active: menu === 'general' }"
        @click="changeTab('general')"
      >
        청약신청
      </div>
    </div>

    <!-- 상단 안내 -->
    <div class="notice notice-emphasis">
      <div class="notice-item bold warning">
        ※ 청약 신청 전 반드시 본인 자격을 확인해 주세요.
      </div>
      <div class="notice-item">
        청약 신청자, 계약자, 입주자는 동일인이어야 하며,
        <span class="important">대리 신청은 불가</span>합니다.
      </div>
      <div class="notice-item">
        특별공급/일반공급 중
        <span class="important">주택형(타입)별로 1건만 신청</span> 가능합니다.
        (중복신청 시 <span class="warning">무효처리</span>)
      </div>
      <div class="notice-item">
        제출서류는 <b>서류심사 대상자에 한해</b> 개별 연락드립니다.
      </div>
    </div>

    <!-- 일반공급 신청 -->
    <div v-if="menu === 'general'">
      <div v-if="isOngoing === null">
        <p class="info-msg">청약 상태 확인 중입니다...</p>
      </div>

      <div v-else-if="isOngoing === false">
        <div class="no-application highlight-red">
          현재 진행중인 청약이 없습니다.
        </div>
      </div>

      <div v-else>
        <div class="application-info">
          <h2 class="application-title">{{ subscription?.title }}</h2>
          <div
            class="application-description"
            v-html="subscription.description"
          ></div>
        </div>

        <!-- 폼 -->
        <div class="form-container">
          <form id="applicationForm" @submit.prevent="submitApplicationForm">
            <!-- ─────────────────────────────────────────────
                 ① 인증 전: 인증 버튼만 노출 (게이트)
            ───────────────────────────────────────────── -->
            <div class="form-group-card" v-if="!isVerified">
              <div class="form-header">휴대폰 본인인증</div>
              <button type="button" @click="startAuth" class="btn btn-auth">
                휴대폰 본인인증
              </button>
              <small class="input-guide"
                >인증 완료 후 신청 단계가 열립니다.</small
              >
            </div>

            <!-- ─────────────────────────────────────────────
                 ② 인증 후: 모든 입력 섹션 표시
            ───────────────────────────────────────────── -->
            <template v-else>
              <!-- 읽기 전용 이름/연락처 -->
              <div class="form-group-card">
                <div class="form-header">
                  청약자 이름 <span class="required">*</span>
                </div>
                <div class="form-input">
                  <input type="text" v-model="form.name" readonly />
                </div>

                <div class="form-header">
                  청약자 연락처 <span class="required">*</span>
                </div>
                <div class="form-input">
                  <input type="text" v-model="form.phone" readonly />
                </div>
              </div>

              <!-- 신청유형 -->
              <div
                class="form-group-card"
                v-if="subscription?.subscriptionType?.length"
              >
                <div class="form-header">
                  <span class="form-number">3.</span>
                  신청유형 <span class="required">*</span>
                </div>
                <div class="form-input radio-group">
                  <label
                    v-for="type in subscription.subscriptionType"
                    :key="type"
                    class="radio-label"
                  >
                    <input
                      type="radio"
                      name="supplyType"
                      :value="type"
                      v-model="form.supplyType"
                      required
                    />
                    <span class="radio-title">{{
                      mapSupplyTypeLabel(type)
                    }}</span>
                  </label>
                </div>
                <small class="input-guide"
                  >한 가구 당 한 번만 신청할 수 있습니다.</small
                >
              </div>

              <!-- 특별공급 추가항목 -->
              <template v-if="form.supplyType === 'SPECIAL-15'">
                <div class="form-group-card highlight-special">
                  <div class="form-header">
                    [특별공급] 아래 항목은 모두 필수 입력입니다.
                  </div>

                  <!-- 소득기준 -->
                  <div>
                    <span class="form-number">4.</span> 소득기준
                    <span class="required">*</span>
                    <div class="form-input radio-group-block">
                      <label
                        ><input
                          type="radio"
                          value="1순위"
                          v-model="form.income"
                          required
                        />
                        1순위(100%)</label
                      >
                      <label
                        ><input
                          type="radio"
                          value="2순위"
                          v-model="form.income"
                          required
                        />
                        2순위(110%)</label
                      >
                      <label
                        ><input
                          type="radio"
                          value="3순위"
                          v-model="form.income"
                          required
                        />
                        3순위(120%)</label
                      >
                    </div>
                  </div>

                  <!-- 지역기준 -->
                  <div>
                    <span class="form-number">5.</span> 지역기준
                    <span class="required">*</span>
                    <div class="form-input radio-group-block">
                      <label
                        ><input
                          type="radio"
                          value="1순위"
                          v-model="form.regional"
                          required
                        />
                        1순위(서울시 관악구)</label
                      >
                      <label
                        ><input
                          type="radio"
                          value="2순위"
                          v-model="form.regional"
                          required
                        />
                        2순위(서울시)</label
                      >
                      <label
                        ><input
                          type="radio"
                          value="3순위"
                          v-model="form.regional"
                          required
                        />
                        3순위(그 외 지역)</label
                      >
                    </div>
                  </div>

                  <!-- 자동차 -->
                  <div>
                    <span class="form-number">6.</span> 자동차 소지 유무
                    <span class="required">*</span>
                    <div class="form-input radio-group-block">
                      <label
                        ><input
                          type="radio"
                          value="Y"
                          v-model="form.car"
                          required
                        />
                        유(2025년 자동차 가액 4,563만원 이내)</label
                      >
                      <label
                        ><input
                          type="radio"
                          value="N"
                          v-model="form.car"
                          required
                        />
                        무</label
                      >
                    </div>
                  </div>

                  <!-- 자산기준 -->
                  <div>
                    <span class="form-number">7.</span> 자산기준
                    <span class="required">*</span>
                    <div class="form-input radio-group-block">
                      <label>
                        <input
                          type="radio"
                          value="Y"
                          v-model="form.assetBasis"
                          required
                        />
                        2025년 자산기준금액 2억5천4백만원을 초과하지 않습니다.
                      </label>
                    </div>
                  </div>
                </div>
              </template>

              <!-- 개인정보 동의 -->
              <div class="agreement highlight-agree">
                <input
                  type="checkbox"
                  id="agreement"
                  name="agreement"
                  v-model="isConsent"
                  required
                />
                <label for="agreement">
                  <span class="required">*</span> 개인정보 수집 및 이용에
                  동의합니다.
                </label>
              </div>

              <!-- 제출 -->
              <div class="button-group">
                <button type="submit" class="btn btn-submit strong">
                  등록
                </button>
              </div>
            </template>
          </form>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import axios from '@/utils/axios'
import router from '@/router'

const menu = ref('general')
const isOngoing = ref(null)
const subscription = ref(null)

const isVerified = ref(false) // ✅ 본인인증 게이트
const isVerifying = ref(false)
const isConsent = ref(false) // 개인정보 동의 상태
let authPopup = null

const form = ref({
  name: '',
  phone: '',
  supplyType: '',
  supplyUid: '',
  // 특별공급
  income: '',
  regional: '',
  car: '',
  assetBasis: '',
})

const changeTab = tab => {
  menu.value = tab
  if (tab === 'general') checkApplicationStatus()
}

const mapSupplyTypeLabel = type => {
  switch (type) {
    case 'GENERAL-15':
      return '15평 청년 일반공급'
    case 'SPECIAL-15':
      return '15평 청년 특별공급'
    case 'GENERAL-30':
      return '30평 청년 & 신혼부부 일반공급'
    default:
      return type
  }
}

// 본인인증 시작
const startAuth = async () => {
  if (isVerifying.value) return

  try {
    isVerifying.value = true
    const { data } = await axios.post('/bx201/nice/prepare', null, {
      withCredentials: true,
    })

    if (!data?.auth_url) {
      throw new Error('auth_url missing')
    }

    authPopup = window.open(
      data.auth_url,
      'popupChk',
      'width=480,height=812,resizable=yes,scrollbars=yes'
    )

    if (!authPopup) {
      throw new Error('popup blocked')
    }
  } catch (e) {
    const message =
      e?.response?.data?.message ||
      '본인인증 준비 중 오류가 발생했습니다. 서버 로그를 확인해 주세요.'
    alert(message)
    isVerifying.value = false
  }
}

// 제출
const submitApplicationForm = async () => {
  // ✅ 수비적 2차 방어
  if (!isVerified.value) {
    alert('휴대폰 본인인증이 필요합니다.')
    return
  }
  if (!isConsent.value) {
    alert('개인정보 수집 및 이용에 동의해 주세요.')
    return
  }

  if (form.value.supplyType === 'SPECIAL-15') {
    if (
      !form.value.income ||
      !form.value.regional ||
      !form.value.car ||
      !form.value.assetBasis
    ) {
      alert('모든 특별공급 항목을 선택해 주세요.')
      return
    }
  }

  const payload = {
    name: form.value.name,
    phone: form.value.phone,
    supplyType: form.value.supplyType,
    supplyUid: form.value.supplyUid,
    isConsentStatus: true,
    income: form.value.supplyType === 'SPECIAL-15' ? form.value.income : null,
    regional:
      form.value.supplyType === 'SPECIAL-15' ? form.value.regional : null,
    car: form.value.supplyType === 'SPECIAL-15' ? form.value.car === 'Y' : null,
    assetBasis:
      form.value.supplyType === 'SPECIAL-15'
        ? form.value.assetBasis === 'Y'
        : null,
  }

  try {
    await axios.post('/user/subscriptionApplicants', payload)
    alert('청약 신청이 완료되었습니다.')
    router.push('/inquiry')
  } catch (error) {
    if (error.response && error.response.status === 409) {
      alert(error.response.data || '이미 신청된 사용자입니다.')
    } else {
      alert('신청 중 오류가 발생했습니다.')
    }
  }
}

// 진행중인 청약 조회
const checkApplicationStatus = async () => {
  try {
    const res = await axios.get('/user/subscription')
    if (res.status === 204 || !res.data?.length) {
      isOngoing.value = false
      alert('현재 진행중인 청약이 없습니다.')
    } else {
      isOngoing.value = true
      subscription.value = res.data[0]
      form.value.supplyUid = subscription.value.id
    }
  } catch (e) {
    isOngoing.value = false
    alert('서버 에러.')
  }
}

const handleNiceMessage = e => {
  if (e.origin !== window.location.origin) return
  if (e.data?.source !== 'bx201-nice-auth') return

  isVerifying.value = false

  if (!e.data.success) {
    alert(e.data?.data?.message || '본인인증 처리 중 오류가 발생했습니다.')
    return
  }

  const payload = e.data.data || {}
  const name = payload.name || payload.user_name || payload.client_name
  const phone =
    payload.phone ||
    payload.mobile_no ||
    payload.mobile_number ||
    payload.di_phone

  if (!name || !phone) {
    alert('본인인증 결과를 확인하지 못했습니다.')
    return
  }

  form.value.name = name
  form.value.phone = String(phone).replace(/[^0-9]/g, '')
  isVerified.value = true
}

// 본인인증 팝업 결과 수신
onMounted(() => {
  window.addEventListener('message', handleNiceMessage)
  checkApplicationStatus()
})

onBeforeUnmount(() => {
  window.removeEventListener('message', handleNiceMessage)
  if (authPopup && !authPopup.closed) {
    authPopup.close()
  }
})
</script>

<style scoped>
.required {
  color: #e53935;
  font-weight: bold;
  margin-left: 3px;
}

.warning {
  color: #e53935;
}
.important {
  color: #007bff;
  font-weight: 500;
}
.notice-emphasis {
  background: #fff4e0;
  border-left: 4px solid #e53935;
  margin-bottom: 14px;
}
.highlight-red {
  color: #e53935;
  font-weight: bold;
}
.input-guide {
  color: #888;
  font-size: 12px;
  margin-top: 2px;
  display: block;
}
.radio-title {
  margin-left: 5px;
}
.radio-label {
  margin-right: 14px;
}
.form-group-card.highlight-special {
  border: 2px solid #007bff;
  background: #f3faff;
}
.agreement.highlight-agree {
  background: #f8f8e7;
  padding: 8px 16px;
  border-radius: 6px;
  margin-top: 18px;
  border: 1px solid #e5e7b5;
}
.btn.strong {
  font-size: 17px;
  font-weight: 600;
  letter-spacing: 1px;
}
.info-msg {
  color: #888;
  text-align: center;
  margin: 24px 0;
}
.container {
  max-width: 1200px;
  margin: 0 auto;
  background-color: #fff;
  padding: 20px;
  border: 1px solid #ddd;
  border-radius: 8px;
}

.tabs {
  display: flex;
  border-bottom: 1px solid #ddd;
}

.tab {
  padding: 15px 20px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
}

.tab.active {
  color: #007bff;
  border-bottom: 2px solid #007bff;
}

.notice {
  padding: 20px;
  font-size: 14px;
  color: #666;
}

.form-container {
  margin-top: 20px;
}

.radio-group-block {
  margin-bottom: 4px;
  display: flex;
  flex-direction: column;
}

.form-group-card {
  background-color: #f9f9f9;
  padding: 15px;
  margin-bottom: 15px;
  border: 1px solid #ddd;
  border-radius: 5px;
}

.form-header {
  margin-bottom: 6px;
  font-weight: 600;
}

.button-group {
  display: flex;
  justify-content: flex-end;
}

.btn {
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.btn-submit:hover {
  background-color: #005ec2;
}

.form-input input[readonly] {
  background-color: #f0f0f0;
  color: #666;
  cursor: not-allowed;
  pointer-events: none; /* 클릭/포커스 등 상호작용 차단 */
  outline: none; /* 포커스 윤곽선 제거 */
}
</style>
